import React, { useState, useEffect, useRef } from "react";
import Section from "./layouts/Section";
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Material-UI Components
import {
    Box,
    Card,
    CardContent,
    Typography,
    Avatar,
    Grid,
    Button,
    Chip,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    Tooltip,
    Container,
    useTheme,
    useMediaQuery,
    alpha,
    Stack,
    Paper,
    Badge,
    Skeleton
} from '@mui/material';

// Material-UI Icons
import {
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    Security as SecurityIcon,
    Lock as LockIcon,
    VerifiedUser as VerifiedUserIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Fingerprint as FingerprintIcon,
    Shield as ShieldIcon,
    AccountCircle as AccountCircleIcon,
    VpnKey as VpnKeyIcon,
    AdminPanelSettings as AdminPanelSettingsIcon
} from '@mui/icons-material';

// Redux and utilities
import { authActions } from '../redux/actions';
import { userSelect, allRemoteSettingsSelect, homeScreenStatSelect } from '../redux/selectors/index';
import { notify } from '../utility/Helper1';
import { staticCountries } from '../utility/countries';
import { Eye, EyeOff, Copy, Wallet, Shield, TrendingDown, AlertCircle, CheckCircle, History, User, Mail, Phone, MapPin, Settings, Key, Smartphone, UserCheck } from 'lucide-react';
import { SiteWide } from "./customComponents";

function PersonalInformation() {
    const userData = useSelector(userSelect);
    const site_settings = useSelector(allRemoteSettingsSelect);
    const finance = useSelector(homeScreenStatSelect);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // State management
    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('Loading...');
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const hiddenFileInput = useRef(null);

    // Modal states
    const [passwordModal, setPasswordModal] = useState(false);
    const [phoneModal, setPhoneModal] = useState(false);

    // Form states
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [withdrawalPin, setWithdrawalPin] = useState('');
    const [phone, setPhone] = useState(userData?.phone ?? '');
    const [country, setCountry] = useState(userData?.country ?? '');
    const [phoneCode, setPhoneCode] = useState('...');

    // UI states
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showPin, setShowPin] = useState(false);
    // Custom theme colors for neon accents
    const neonColors = {
        primary: '#00D4FF',
        secondary: '#7C3AED',
        accent: '#F59E0B',
        success: '#10B981',
        warning: '#F59E0B',
        error: '#EF4444',
        surface: '#1F2937',
        surfaceVariant: '#374151'
    };

    // Password change handler
    const handlePasswordChange = async (e) => {
        e.preventDefault();

        if (!password || !confirmPassword) {
            notify('Please fill in all password fields', 0);
            return;
        }

        if (password !== confirmPassword) {
            notify('Passwords do not match', 0);
            return;
        }

        if (password.length < 8) {
            notify('Password must be at least 8 characters long', 0);
            return;
        }

        if (!withdrawalPin) {
            notify('Please enter your PIN', 0);
            return;
        }

        setLoading(true);
        setLoadingText('Updating password...');

        dispatch(authActions.onLogin("account/change_password", { password, pin: withdrawalPin }, response => {
            if (response.code === 1) {
                setPassword('');
                setConfirmPassword('');
                setWithdrawalPin('');
                setPasswordModal(false);
            }
            notify(response.msg, response.code);
            setLoading(false);
        }));
    };

    // Phone change handler
    const handlePhoneChange = async (e) => {
        e.preventDefault();

        if (!phone || !country) {
            notify('Please fill in all phone fields', 0);
            return;
        }

        if (!withdrawalPin) {
            notify('Please enter your PIN', 0);
            return;
        }

        setLoading(true);
        setLoadingText('Updating phone number...');

        dispatch(authActions.onLogin("account/update_phone", { country, phone, pin: withdrawalPin }, response => {
            if (response.code === 1) {
                setWithdrawalPin('');
                setPhoneModal(false);
            }
            notify(response.msg, response.code);
            setLoading(false);
        }));
    };

    // Country change handler
    const handleCountryChange = (countryId) => {
        const selectedCountry = staticCountries.find(e => e.id == countryId);
        setCountry(countryId);
        setPhoneCode(selectedCountry?.phonecode || '...');
    };

    // Avatar upload functionality
    const progClickFileUpload = () => {
        hiddenFileInput.current?.click();
    };

    const uploadAvatar = async (file) => {
        setUploadingAvatar(true);
        // Simulate upload

        dispatch(authActions.onLogin("account/change-avatar", { avatar: file }, response => {
            setUploadingAvatar(false)
        }),
        )
    };

    // Calculate profile completeness
    const calculateProfileCompleteness = () => {
        let score = 0;
        const total = 6;

        if (userData?.email) score++;
        if (userData?.phone) score++;
        if (userData?.country) score++;
        if (userData?.bank != '') score++;
        if (userData?.usdtbscwallet != '') score++;
        if (userData?.emailVerification === 1) score++;

        return Math.round((score / total) * 100);
    };


    // Initialize component
    useEffect(() => {
        handleCountryChange(country);
    }, []);

    // Load homepage data
    useEffect(() => {
        if (userData?.emailVerification === 1) {
            const timing = setTimeout(() => {
                dispatch(authActions.onGetHomePageData("user/load/homepage", response => {
                    // Handle response if needed
                }));
            }, 1000);

            return () => clearTimeout(timing);
        }
    }, [userData?.emailVerification, dispatch]);

    // Security settings menu items
    const securityMenuItems = [
        {
            icon: Key,
            title: 'Change Password',
            description: 'Update your account password for enhanced security',
            action: () => navigate('/change-password'),
            color: neonColors.primary,
            gradient: 'from-cyan-400 to-blue-500'
        },
        {
            icon: Phone,
            title: 'Update Phone Number',
            description: 'Modify your registered phone number',
            action: () => navigate('/phone'),
            color: neonColors.success,
            gradient: 'from-green-400 to-emerald-500'
        },
        {
            icon: Shield,
            title: 'Two-Factor Authentication',
            description: 'Enable 2FA for additional account security',
            action: () => navigate('/change-pin'),
            color: neonColors.warning,
            gradient: 'from-amber-400 to-orange-500'
        }
    ];

    // Account information items
    const accountInfoItems = [
        {
            icon: User,
            label: 'Username',
            value: userData?.username || 'Not set',
            color: neonColors.primary,
            verified: !!userData?.username
        },
        {
            icon: Mail,
            label: 'Email Address',
            value: userData?.email || 'Not verified',
            color: neonColors.success,
            verified: userData?.emailVerification === 1
        },
        {
            icon: Phone,
            label: 'Phone Number',
            value: userData?.phone || 'Not set',
            color: neonColors.warning,
            verified: !!userData?.phone
        },
        {
            icon: MapPin,
            label: 'Country',
            value: userData?.countryName || 'Not set',
            color: neonColors.secondary,
            verified: !!userData?.countryName
        }
    ];

    const profileCompleteness = calculateProfileCompleteness();

    return (
        <SiteWide id="main" title="Profile">
            <Section loading={loading} overlayText={loadingText} title="Account Management">
                <div className="min-h-screen p-4">
                    <div className="max-w-7xl mx-auto pt-8">
                        {/* Modern Header */}
                        <div className="text-center mb-12">
                            <h1 className="text-5xl font-bold text-white mb-4 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                                Account Management
                            </h1>
                            <p className="text-xl text-blue-200">
                                Manage your personal information and security settings
                            </p>
                        </div>

                        <div className="flex flex-col gap-8">
                            {/* Enhanced Profile Header Card */}
                            <div className="backdrop-blur-xl bg-white/10 rounded-3xl border border-white/20 p-8 shadow-2xl">
                                <div className="relative overflow-hidden">
                                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500"></div>
                                    <div className="flex flex-col md:flex-row items-center gap-6 p-6">
                                        <div className="relative">
                                            <div className="relative">
                                                {uploadingAvatar && (
                                                    <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center z-10">
                                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                                                    </div>
                                                )}
                                                <div 
                                                    className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-2xl cursor-pointer hover:scale-105 transition-all duration-300"
                                                    onClick={progClickFileUpload}
                                                    style={{
                                                        backgroundImage: userData?.image ? `url(${userData.image})` : undefined,
                                                        backgroundSize: 'cover',
                                                        backgroundPosition: 'center'
                                                    }}
                                                >
                                                    {!userData?.image && (userData?.username?.charAt(0)?.toUpperCase() || 'U')}
                                                </div>
                                                {userData?.emailVerification === 1 ? (
                                                    <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                                                        <CheckCircle className="text-white" size={20} />
                                                    </div>
                                                ) : (
                                                    <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center shadow-lg">
                                                        <AlertCircle className="text-white" size={20} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex-1 text-center md:text-left">
                                            <h2 className="text-4xl font-bold text-white mb-2 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                                                {userData?.username || 'User Account'}
                                            </h2>
                                            <p className="text-xl text-gray-300 mb-6">
                                                {userData?.email}
                                            </p>

                                            {/* Profile Completeness Section */}
                                            <div className="mb-6">
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="text-white font-medium">Completeness</span>
                                                    <span className={`text-2xl font-bold ${profileCompleteness >= 80 ? 'text-green-400' :
                                                            profileCompleteness >= 50 ? 'text-yellow-400' : 'text-red-400'
                                                        }`}>
                                                        {profileCompleteness}%
                                                    </span>
                                                </div>
                                                <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full transition-all duration-600 ${profileCompleteness >= 80 ? 'bg-gradient-to-r from-green-400 to-green-500' :
                                                                profileCompleteness >= 50 ? 'bg-gradient-to-r from-yellow-400 to-yellow-500' :
                                                                    'bg-gradient-to-r from-red-400 to-red-500'
                                                            }`}
                                                        style={{ width: `${profileCompleteness}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-3">
                                            <div className="inline-flex items-center px-4 py-2 rounded-2xl border-2 border-blue-400 bg-blue-500/20 text-blue-300">
                                                <Shield className="mr-2" size={20} />
                                                <span className="font-semibold">Security Level: High</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Enhanced Account Information Card */}
                            <div className="backdrop-blur-xl bg-white/10 rounded-3xl border border-white/20 p-8 shadow-2xl">
                                <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                                        <User className="text-white" size={24} />
                                    </div>
                                    Account Information
                                </h2>

                                <div className="space-y-4">
                                    {accountInfoItems.map((item, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center p-4 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-300 hover:translate-x-2"
                                        >
                                            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mr-4 shadow-lg ${item.label === 'Username' ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
                                                    item.label === 'Email Address' ? 'bg-gradient-to-br from-green-500 to-green-600' :
                                                        item.label === 'Phone Number' ? 'bg-gradient-to-br from-yellow-500 to-yellow-600' :
                                                            'bg-gradient-to-br from-purple-500 to-purple-600'
                                                }`}>
                                                <item.icon className="text-white" size={22} />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="text-white font-semibold text-lg">{item.label}</h3>
                                                <p className="text-gray-300 text-sm">{item.value}</p>
                                            </div>
                                            {item.verified && (
                                                <div className="flex items-center">
                                                    <CheckCircle className="text-green-400" size={24} />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Enhanced Security Settings Card */}
                            <div className="backdrop-blur-xl bg-white/10 rounded-3xl border border-white/20 p-8 shadow-2xl">
                                <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                                        <Shield className="text-white" size={24} />
                                    </div>
                                    Security Settings
                                </h2>

                                <div className="space-y-4">
                                    {securityMenuItems.map((item, index) => (
                                        <div
                                            key={index}
                                            onClick={item.action}
                                            className="p-6 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl cursor-pointer"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg ${item.title === 'Change Password' ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
                                                        item.title === 'Update Phone Number' ? 'bg-gradient-to-br from-green-500 to-green-600' :
                                                            'bg-gradient-to-br from-yellow-500 to-yellow-600'
                                                    }`}>
                                                    <item.icon className="text-white" size={24} />
                                                </div>
                                                <div className="flex-1">
                                                    <h3 className="text-white font-semibold text-lg mb-1">{item.title}</h3>
                                                    <p className="text-gray-300 text-sm">{item.description}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Enhanced Password Change Dialog */}
                        {passwordModal && (
                            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setPasswordModal(false)}></div>
                                <div className="relative w-full max-w-md bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-8 shadow-2xl">
                                    <div className="flex items-center mb-6">
                                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                                            <Key className="text-white" size={20} />
                                        </div>
                                        <h2 className="text-2xl font-bold text-white">Change Password</h2>
                                    </div>
                                    <form onSubmit={handlePasswordChange} className="space-y-6">
                                        <div>
                                            <label className="block text-white font-medium mb-2">New Password</label>
                                            <div className="relative">
                                                <input
                                                    type={showPassword ? 'text' : 'password'}
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    className="w-full bg-white/10 border-2 border-white/20 rounded-2xl px-6 py-4 pr-16 text-white text-lg placeholder-gray-300 focus:outline-none focus:border-blue-400 transition-all duration-300"
                                                    placeholder="Enter new password"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-300 hover:text-white transition-colors"
                                                >
                                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                                </button>
                                            </div>
                                            <p className="text-gray-400 text-sm mt-2">Password must be at least 8 characters long</p>
                                        </div>
                                        <div>
                                            <label className="block text-white font-medium mb-2">Confirm Password</label>
                                            <div className="relative">
                                                <input
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    className="w-full bg-white/10 border-2 border-white/20 rounded-2xl px-6 py-4 pr-16 text-white text-lg placeholder-gray-300 focus:outline-none focus:border-blue-400 transition-all duration-300"
                                                    placeholder="Confirm new password"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-300 hover:text-white transition-colors"
                                                >
                                                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                                </button>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-white font-medium mb-2">PIN</label>
                                            <div className="relative">
                                                <input
                                                    type={showPin ? 'text' : 'password'}
                                                    value={withdrawalPin}
                                                    onChange={(e) => setWithdrawalPin(e.target.value)}
                                                    maxLength={4}
                                                    className="w-full bg-white/10 border-2 border-white/20 rounded-2xl px-6 py-4 pr-16 text-white text-lg placeholder-gray-300 focus:outline-none focus:border-yellow-400 transition-all duration-300"
                                                    placeholder="Enter your 4-digit PIN"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPin(!showPin)}
                                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-300 hover:text-white transition-colors"
                                                >
                                                    {showPin ? <EyeOff size={20} /> : <Eye size={20} />}
                                                </button>
                                            </div>
                                            <p className="text-gray-400 text-sm mt-2">Enter your 4-digit PIN to confirm</p>
                                        </div>

                                        <div className="flex gap-4 pt-4">
                                            <button
                                                type="button"
                                                onClick={() => setPasswordModal(false)}
                                                className="flex-1 py-3 px-6 bg-white/10 border border-white/20 rounded-2xl text-white hover:bg-white/20 transition-all duration-300"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={loading}
                                                className={`flex-1 py-3 px-6 rounded-2xl font-semibold text-white transition-all duration-300 ${loading
                                                        ? 'bg-gray-500 cursor-not-allowed'
                                                        : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 hover:scale-105 shadow-lg hover:shadow-blue-500/25'
                                                    }`}
                                            >
                                                {loading ? (
                                                    <div className="flex items-center justify-center">
                                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                        Updating...
                                                    </div>
                                                ) : (
                                                    'Update Password'
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}
                        {/* Enhanced Phone Change Dialog */}
                        {phoneModal && (
                            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setPhoneModal(false)}></div>
                                <div className="relative w-full max-w-md bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-8 shadow-2xl">
                                    <div className="flex items-center mb-6">
                                        <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-600 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                                            <Phone className="text-white" size={20} />
                                        </div>
                                        <h2 className="text-2xl font-bold text-white">Update Phone Number</h2>
                                    </div>
                                    <form onSubmit={handlePhoneChange} className="space-y-6">
                                        <div>
                                            <label className="block text-white font-medium mb-2">Country</label>
                                            <select
                                                value={country}
                                                onChange={(e) => handleCountryChange(e.target.value)}
                                                className="w-full bg-white/10 border-2 border-white/20 rounded-2xl px-6 py-4 text-white text-lg focus:outline-none focus:border-green-400 transition-all duration-300"
                                            >
                                                {staticCountries.map((country) => (
                                                    <option key={country.id} value={country.id} className="bg-gray-800 text-white">
                                                        {country.name} (+{country.phonecode})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-white font-medium mb-2">Phone Number</label>
                                            <input
                                                type="text"
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value)}
                                                placeholder={`${phoneCode} XXX XXX XXX`}
                                                className="w-full bg-white/10 border-2 border-white/20 rounded-2xl px-6 py-4 text-white text-lg placeholder-gray-300 focus:outline-none focus:border-green-400 transition-all duration-300"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-white font-medium mb-2">PIN</label>
                                            <div className="relative">
                                                <input
                                                    type={showPin ? 'text' : 'password'}
                                                    value={withdrawalPin}
                                                    onChange={(e) => setWithdrawalPin(e.target.value)}
                                                    maxLength={4}
                                                    className="w-full bg-white/10 border-2 border-white/20 rounded-2xl px-6 py-4 pr-16 text-white text-lg placeholder-gray-300 focus:outline-none focus:border-yellow-400 transition-all duration-300"
                                                    placeholder="Enter your 4-digit PIN"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPin(!showPin)}
                                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-300 hover:text-white transition-colors"
                                                >
                                                    {showPin ? <EyeOff size={20} /> : <Eye size={20} />}
                                                </button>
                                            </div>
                                            <p className="text-gray-400 text-sm mt-2">Enter your 4-digit PIN to confirm</p>
                                        </div>

                                        <div className="flex gap-4 pt-4">
                                            <button
                                                type="button"
                                                onClick={() => setPhoneModal(false)}
                                                className="flex-1 py-3 px-6 bg-white/10 border border-white/20 rounded-2xl text-white hover:bg-white/20 transition-all duration-300"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={loading}
                                                className={`flex-1 py-3 px-6 rounded-2xl font-semibold text-white transition-all duration-300 ${loading
                                                        ? 'bg-gray-500 cursor-not-allowed'
                                                        : 'bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 hover:scale-105 shadow-lg hover:shadow-green-500/25'
                                                    }`}
                                            >
                                                {loading ? (
                                                    <div className="flex items-center justify-center">
                                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                        Updating...
                                                    </div>
                                                ) : (
                                                    'Update Phone'
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}

                        {/* Hidden file input for avatar upload */}
                        <input
                            ref={hiddenFileInput}
                            type="file"
                            accept=".jpg,.gif,.png,.jpeg"
                            onChange={(e) => uploadAvatar(e.target.files[0])}
                            style={{ display: 'none' }}
                        />
                    </div>
                </div>
            </Section>
        </SiteWide>
    );
}

export default PersonalInformation;